import { DynamicLoaderRegistry } from '@tencent/workbench';
import { loadTask } from '@framework/workbench/common/service/tasks/load-task';
import type { IBaseDrawerFeature } from '@src/framework/workbench/common/feature/base-drawer-interface';
import type { NumberInputFrom } from '@tencent/wb-ext-form';
import { ILocks } from '@src/slide/domain/ui-model/element-modeles/property/lock/lock.interface';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { ImageRelativeResizeType, IOriginalSize } from '@src/slide/domain/ui-model/element-modeles/property/image-size/image-size.interface';

export const WIDTH = 'width';
export const HEIGHT = 'height';
export type IType = 'width' | 'height';

export interface ISizeFeature extends IBaseDrawerFeature {
    /**
     * 锁定纵横比
     */
    readonly size: ILocks | undefined;
    /**
     * 高度。多选不同高度元素，值为NaN
     */
    readonly height: number;
    /**
     * 宽度。多选不同宽度元素，值为NaN
     */
    readonly width: number;
    /**
     * 缩放高度。图片的缩放基准默认相对原尺寸，其余元素默认相对选中时的大小。多选不同缩放高度的图片时，值为NaN
     */
    scaleHeight: number;
    /**
     * 缩放宽度。图片的缩放基准默认相对原尺寸，其余元素默认相对选中时的大小。多选不同缩放宽度的图片时，值为NaN
     */
    scaleWidth: number;
    /**
     * 记录元素的原始大小。key为model的key，value为原始宽度和高度。
     */
    modelOriginalSizeMap: IModelOriginalSizeMap;
    /**
     * 是否为幻灯片最佳放映比例
     */
    isBestPlayScale: boolean;
    /**
     * 分辨率
     */
    resolution: string;
    /**
     * 重设按钮是否置灰。当原始尺寸大小显示为空时需要置灰
     */
    shouldResetDisabled: boolean;
    /**
     * 高度变化
     */
    handleHeightChange(height: number, from: NumberInputFrom);
    /**
     * 宽度变化
     */
    handleWidthChange(width: number, from: NumberInputFrom);
    /**
     * 锁定纵横比变化
     */
    handleLocksChange(value: boolean): void;
    /**
     * 获取高度/宽度的options。options是动态的，由于表格的min和max由其行列数决定
     */
    getOptions(type: IType);
    /**
     * 获取缩放高度/宽度的options。options是动态的，min为1%, max为 50cm / 原始大小。表格的min为其 最小值 / 原始大小。
     */
    getElementSizeScaleOptions(type: IType);
    /**
     * 缩放高度/宽度变化
     */
    handleElementSizeScaleChange(value: number, type: IType, from: NumberInputFrom): void;
    /**
     * 获取原始尺寸内容。只要选中元素有图片 就取第一个图片的原始尺寸值。
     */
    getOriginalSizeContent(): string;
    /**
     * 幻灯片最佳放映比例变化
     */
    handleBestPlayScale(value: boolean): void;
    /**
     * 初始化 幻灯片最佳放映比例变化。
     * 切换选中元素时调用，每次切换元素，幻灯片最佳放映比例需取消勾选，且分辨率置为默认值。
     */
    initBestPlayScale(): void;
    /**
     * 分辨率变化
     */
    handleResolution(value: string): void;
    /**
     * 相对图片原始尺寸
     */
    getRelativeImageOriginalSize(): ImageRelativeResizeType;
    /**
     * 相对图片原始尺寸变化
     */
    handleRelativeImageOriginalSize(value: boolean): void;
    /**
     * 重设图片为原始尺寸
     */
    handleResetOriginalSize(): void;

    initScaleInfo(): void;

    getChangeVal(value: number, type: IType, isFromInput: boolean): number
}

export type IModelOriginalSizeMap = Map<IBaseModel['key'], IOriginalSize>;
export interface IScaleInfo {
    scaleHeight: number;
    scaleWidth: number;
}

export const ISizeFeature = Symbol.for('feature:size');

DynamicLoaderRegistry.merge(ISizeFeature, {
    useFactory: async () => {
        const { SizeFeature } = await import('./size-feature');
        return SizeFeature;
    },
    on: loadTask,
});
